/**
 * NotificationPreferencesDialog - Modal for managing notification preferences
 * for an individual ECM member: pause/resume, escalation cap, channel toggles,
 * frequency caps, quiet hours, and full opt-out.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Bell,
  BellOff,
  AlertCircle,
  MessageSquare,
  Mail,
  Smartphone,
  Moon,
  Shield,
} from 'lucide-react';
import { toast } from 'sonner';
import { EscalationTierExplainer } from './EscalationTierExplainer';
import { useMemberNotificationPreferences } from '@/hooks/useEcmReminders';
import type { MemberNotificationPreferencesUpdate } from '@/services/api/ecmReminders';

interface NotificationPreferencesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  memberId: number;
  memberName: string;
  onPreferencesUpdated?: () => void;
}

const ESCALATION_OPTIONS: { value: number | null; label: string }[] = [
  { value: null, label: 'Default' },
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 0, label: 'Off' },
];

const TIMEZONE_OPTIONS = [
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Anchorage',
  'Pacific/Honolulu',
];

export const NotificationPreferencesDialog: React.FC<NotificationPreferencesDialogProps> = ({
  isOpen,
  onClose,
  memberId,
  memberName,
  onPreferencesUpdated,
}) => {
  const { preferences, loading, saving, updatePreferences } =
    useMemberNotificationPreferences(isOpen ? memberId : undefined);

  // Local form state
  const [paused, setPaused] = useState(false);
  const [pausedReason, setPausedReason] = useState('');
  const [escalationTier, setEscalationTier] = useState<number | null>(null);
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [inAppEnabled, setInAppEnabled] = useState(true);
  const [maxPerDay, setMaxPerDay] = useState<number>(3);
  const [maxPerWeek, setMaxPerWeek] = useState<number>(10);
  const [quietStart, setQuietStart] = useState('21:00');
  const [quietEnd, setQuietEnd] = useState('08:00');
  const [timezone, setTimezone] = useState('America/Los_Angeles');
  const [showOptOut, setShowOptOut] = useState(false);

  // Sync form with fetched preferences
  useEffect(() => {
    if (preferences) {
      setPaused(preferences.notification_paused);
      setPausedReason(preferences.paused_reason || '');
      setEscalationTier(preferences.max_escalation_tier);
      setSmsEnabled(preferences.sms_enabled);
      setEmailEnabled(preferences.email_enabled);
      setInAppEnabled(preferences.in_app_enabled);
      setMaxPerDay(preferences.max_reminders_per_day ?? 3);
      setMaxPerWeek(preferences.max_reminders_per_week ?? 10);
      setQuietStart(preferences.quiet_start || '21:00');
      setQuietEnd(preferences.quiet_end || '08:00');
      setTimezone(preferences.timezone || 'America/Los_Angeles');
      setShowOptOut(false);
    }
  }, [preferences]);

  const handleSave = async () => {
    const data: MemberNotificationPreferencesUpdate = {
      notification_paused: paused,
      paused_reason: paused ? (pausedReason || 'Manual pause') : undefined,
      max_escalation_tier: escalationTier ?? undefined,
      sms_enabled: smsEnabled,
      email_enabled: emailEnabled,
      in_app_enabled: inAppEnabled,
      max_reminders_per_day: maxPerDay,
      max_reminders_per_week: maxPerWeek,
      quiet_start: quietStart,
      quiet_end: quietEnd,
      timezone,
    };

    // Clear escalation tier override when set to "Default"
    if (escalationTier === null && preferences?.max_escalation_tier !== null) {
      // Send explicit null — backend handles this as "use program default"
      // Since the API field is Optional[int], we send the value only when it changes
    }

    const result = await updatePreferences(data);
    if (result) {
      toast.success(`Notification preferences updated for ${memberName}`);
      onPreferencesUpdated?.();
      onClose();
    } else {
      toast.error('Failed to update preferences');
    }
  };

  const handleOptOut = async () => {
    const result = await updatePreferences({ opted_out: true });
    if (result) {
      toast.success(`${memberName} opted out of all notifications`, {
        description: 'All pending reminders have been cancelled.',
      });
      onPreferencesUpdated?.();
      onClose();
    } else {
      toast.error('Failed to opt out');
    }
  };

  const handleResumeOptOut = async () => {
    const result = await updatePreferences({ opted_out: false });
    if (result) {
      toast.success(`${memberName} re-enabled for notifications`);
      onPreferencesUpdated?.();
    } else {
      toast.error('Failed to resume notifications');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-blue-600" />
            Notification Preferences
          </DialogTitle>
          <DialogDescription>
            Manage notification channels, escalation level, and frequency for this member.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-gray-400">Loading preferences...</div>
        ) : (
          <div className="space-y-5 mt-2">
            {/* Member info */}
            <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <span className="text-sm text-gray-500">Member:</span>
              <span className="text-sm font-medium">{memberName}</span>
              {preferences?.opted_out && (
                <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                  Opted Out
                </Badge>
              )}
            </div>

            {/* Opted-out banner */}
            {preferences?.opted_out && (
              <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                <div className="flex items-center gap-2">
                  <BellOff className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-700 dark:text-red-300 font-medium">
                    This member is opted out of all notifications
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs border-red-200 text-red-700 hover:bg-red-100"
                  onClick={handleResumeOptOut}
                  disabled={saving}
                >
                  Re-enable
                </Button>
              </div>
            )}

            {/* Master pause toggle */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BellOff className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Pause All Notifications
                  </span>
                </div>
                <Switch
                  checked={paused}
                  onCheckedChange={setPaused}
                  disabled={preferences?.opted_out}
                />
              </div>
              {paused && (
                <input
                  type="text"
                  value={pausedReason}
                  onChange={(e) => setPausedReason(e.target.value)}
                  placeholder="Reason for pausing (optional)..."
                  className="w-full px-3 py-1.5 border rounded-md text-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  maxLength={100}
                />
              )}
            </div>

            {/* Escalation level */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Escalation Level
                </span>
              </div>
              <div className="flex gap-1">
                {ESCALATION_OPTIONS.map((opt) => {
                  const isSelected =
                    (opt.value === null && escalationTier === null) ||
                    opt.value === escalationTier;
                  return (
                    <button
                      key={opt.label}
                      type="button"
                      onClick={() => setEscalationTier(opt.value)}
                      disabled={preferences?.opted_out}
                      className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                        isSelected
                          ? opt.value === 0
                            ? 'border-red-400 bg-red-50 text-red-700 dark:bg-red-900/30 dark:border-red-600 dark:text-red-300'
                            : 'border-blue-400 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:border-blue-600 dark:text-blue-300'
                          : 'border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40'
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <EscalationTierExplainer memberCap={escalationTier} />
            </div>

            {/* Channel toggles */}
            <div className="space-y-3">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Channels
              </span>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">SMS</span>
                  </div>
                  <Switch
                    checked={smsEnabled}
                    onCheckedChange={setSmsEnabled}
                    disabled={preferences?.opted_out || paused}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Email</span>
                  </div>
                  <Switch
                    checked={emailEnabled}
                    onCheckedChange={setEmailEnabled}
                    disabled={preferences?.opted_out || paused}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">In-App</span>
                  </div>
                  <Switch
                    checked={inAppEnabled}
                    onCheckedChange={setInAppEnabled}
                    disabled={preferences?.opted_out || paused}
                  />
                </div>
              </div>
            </div>

            {/* Frequency caps */}
            <div className="space-y-3">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Frequency Caps
              </span>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">
                    Max per day
                  </label>
                  <input
                    type="number"
                    value={maxPerDay}
                    onChange={(e) => setMaxPerDay(Math.max(1, Math.min(50, Number(e.target.value))))}
                    min={1}
                    max={50}
                    disabled={preferences?.opted_out || paused}
                    className="w-full px-3 py-1.5 border rounded-md text-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 disabled:opacity-40"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">
                    Max per week
                  </label>
                  <input
                    type="number"
                    value={maxPerWeek}
                    onChange={(e) => setMaxPerWeek(Math.max(1, Math.min(200, Number(e.target.value))))}
                    min={1}
                    max={200}
                    disabled={preferences?.opted_out || paused}
                    className="w-full px-3 py-1.5 border rounded-md text-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 disabled:opacity-40"
                  />
                </div>
              </div>
            </div>

            {/* Quiet hours */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Moon className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Quiet Hours
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Start</label>
                  <input
                    type="time"
                    value={quietStart}
                    onChange={(e) => setQuietStart(e.target.value)}
                    disabled={preferences?.opted_out || paused}
                    className="w-full px-2 py-1.5 border rounded-md text-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 disabled:opacity-40"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">End</label>
                  <input
                    type="time"
                    value={quietEnd}
                    onChange={(e) => setQuietEnd(e.target.value)}
                    disabled={preferences?.opted_out || paused}
                    className="w-full px-2 py-1.5 border rounded-md text-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 disabled:opacity-40"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Timezone</label>
                  <select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    disabled={preferences?.opted_out || paused}
                    className="w-full px-2 py-1.5 border rounded-md text-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 disabled:opacity-40"
                  >
                    {TIMEZONE_OPTIONS.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz.replace('America/', '').replace('Pacific/', '').replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Opt out section */}
            {!preferences?.opted_out && (
              <div className="border-t pt-4 dark:border-gray-700">
                {!showOptOut ? (
                  <button
                    type="button"
                    onClick={() => setShowOptOut(true)}
                    className="text-xs text-red-500 hover:text-red-700 transition-colors"
                  >
                    Opt out of all notifications...
                  </button>
                ) : (
                  <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs text-red-700 dark:text-red-300 mb-2">
                        This will permanently disable all notifications for {memberName} and cancel all
                        pending reminders. This can be reversed later.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs border-red-300 text-red-700 hover:bg-red-100"
                          onClick={handleOptOut}
                          disabled={saving}
                        >
                          {saving ? 'Processing...' : 'Confirm Opt Out'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs"
                          onClick={() => setShowOptOut(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2 border-t dark:border-gray-700">
              <Button variant="outline" onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving || preferences?.opted_out}>
                {saving ? 'Saving...' : 'Save Preferences'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
